<template>
  <div class="repeat-order">
    <!-- Основная кнопка -->
    <UiKitButton
      v-if="!showChoice"
      color="warning"
      variant="flat"
      size="lg"
      radius="lg"
      full-width
      :loading="isRepeating"
      :disabled="!hasItems"
      @click="onMainClick"
    >
      <template #leading>
        <UIcon name="i-heroicons-arrow-path" class="h-5 w-5 shrink-0" />
      </template>
      Повторить заказ
    </UiKitButton>

    <!-- Выбор поведения при непустой корзине -->
    <div v-else class="repeat-order__choice">
      <p class="repeat-order__choice-text">
        В корзине уже есть товары. Что сделать?
      </p>
      <div class="repeat-order__choice-actions">
        <UiKitButton
          color="warning"
          variant="solid"
          size="md"
          radius="lg"
          full-width
          :loading="isRepeating"
          @click="run('replace')"
        >
          Очистить и заменить
        </UiKitButton>
        <UiKitButton
          color="warning"
          variant="flat"
          size="md"
          radius="lg"
          full-width
          :loading="isRepeating"
          @click="run('append')"
        >
          Добавить к текущим
        </UiKitButton>
        <UiKitButton
          color="default"
          variant="light"
          size="md"
          radius="lg"
          full-width
          :disabled="isRepeating"
          @click="showChoice = false"
        >
          Отмена
        </UiKitButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRepeatOrder, type RepeatOrderMode, type RepeatOrderResult, type RepeatSkippedItem } from '@features/order-history'
import { useCartStore } from '@entities/order'
import type { Order } from '@entities/order'
import { useModalStore, useAlertStore } from '@shared/model'

interface Props {
  order: Order | null
}

const props = defineProps<Props>()

const { isRepeating, repeatOrder } = useRepeatOrder()
const cartStore = useCartStore()
const modalStore = useModalStore()
const alertStore = useAlertStore()

const showChoice = ref(false)

/** В заказе есть позиции для повтора */
const hasItems = computed(() => Array.isArray(props.order?.items) && props.order!.items.length > 0)

function onMainClick(): void {
  if (!hasItems.value) return
  // Если в корзине уже есть товары — спрашиваем пользователя
  if (cartStore.cart.length > 0) {
    showChoice.value = true
    return
  }
  void run('append')
}

const SKIP_REASON_TEXT: Record<RepeatSkippedItem['reason'], string> = {
  unavailable: 'нет в меню',
  stop_list: 'нет в наличии',
  combo_no_data: 'набор недоступен',
}

function buildSkippedMessage(skipped: RepeatSkippedItem[]): string {
  return skipped
    .map(s => `${s.name} (${SKIP_REASON_TEXT[s.reason]})`)
    .join(', ')
}

async function run(mode: RepeatOrderMode): Promise<void> {
  showChoice.value = false
  let result: RepeatOrderResult
  try {
    result = await repeatOrder(props.order, mode)
  } catch (error: any) {
    alertStore.error(error?.message || 'Не удалось повторить заказ')
    return
  }

  // Ничего не добавлено
  if (result.addedCount === 0) {
    if (result.skipped.length > 0) {
      alertStore.error(`Не удалось добавить товары: ${buildSkippedMessage(result.skipped)}`)
    } else {
      alertStore.info('В заказе нет позиций для повтора')
    }
    return
  }

  // Часть позиций не перенесена — предупреждаем, но продолжаем
  if (result.skipped.length > 0) {
    alertStore.warn(`Не добавлены: ${buildSkippedMessage(result.skipped)}`, 7000)
  }

  alertStore.success('Товары добавлены в корзину')

  // Закрываем модалку заказа и открываем корзину
  modalStore.close()
  cartStore.openBasket()
}
</script>

<style scoped lang="scss">
.repeat-order {
  @apply w-full;

  &__choice {
    @apply flex flex-col gap-3 p-4 rounded-hui-lg bg-hui-content2;
  }

  &__choice-text {
    @apply text-sm font-semibold text-hui-foreground;
  }

  &__choice-actions {
    @apply flex flex-col gap-2;
  }
}
</style>
